"use client";

import { useEffect, useState } from "react";

import {
  GAM_BASE_DESKTOP_BANNER_AD_UNIT,
  GAM_BASE_DESKTOP_BANNER_ENABLED,
  GAM_BASE_DESKTOP_BANNER_MIN_HEIGHT,
  GAM_BASE_DESKTOP_BANNER_SIZES,
  GAM_BASE_TABLET_BANNER_AD_UNIT,
  GAM_BASE_TABLET_BANNER_ENABLED,
  GAM_BASE_TABLET_BANNER_MIN_HEIGHT,
  GAM_BASE_TABLET_BANNER_SIZES,
} from "@/components/ads/config";
import GamDisplaySlot from "@/components/ads/GamDisplaySlot";

import styles from "./BaseArticle.module.css";

type TargetingValue =
  | string
  | number
  | boolean
  | Array<string | number | boolean>;

type Props = {
  targeting?: Record<string, TargetingValue | null | undefined>;
};

const TABLET_QUERY = "(min-width: 768px) and (max-width: 1279px)";
const DESKTOP_QUERY = "(min-width: 1280px)";

export default function BaseTabletDesktopBannerAd({ targeting }: Props) {
  const [device, setDevice] = useState<"tablet" | "desktop" | null>(null);

  useEffect(() => {
    const tabletQuery = window.matchMedia(TABLET_QUERY);
    const desktopQuery = window.matchMedia(DESKTOP_QUERY);

    const update = () => {
      if (desktopQuery.matches) {
        setDevice("desktop");
        return;
      }

      if (tabletQuery.matches) {
        setDevice("tablet");
        return;
      }

      setDevice(null);
    };

    update();
    tabletQuery.addEventListener("change", update);
    desktopQuery.addEventListener("change", update);

    return () => {
      tabletQuery.removeEventListener("change", update);
      desktopQuery.removeEventListener("change", update);
    };
  }, []);

  if (device === "tablet") {
    if (!GAM_BASE_TABLET_BANNER_ENABLED) return null;

    return (
      <div className={styles.bannerAdWrap} data-base-banner-device="tablet">
        <div className={styles.bannerAdBox}>
          <div className={styles.adHeader}>
            <span className={styles.sponsored}>Sponsored</span>
          </div>

          <GamDisplaySlot
            id="base-tablet-banner"
            adUnit={GAM_BASE_TABLET_BANNER_AD_UNIT}
            sizes={[...GAM_BASE_TABLET_BANNER_SIZES]}
            minHeight={GAM_BASE_TABLET_BANNER_MIN_HEIGHT}
            targeting={{
              page_type: "base",
              device: "tablet",
              position: "before_strengths",
              placement: "base_tablet_banner_before_strengths",
              ...targeting,
            }}
          />
        </div>
      </div>
    );
  }

  if (device === "desktop") {
    if (!GAM_BASE_DESKTOP_BANNER_ENABLED) return null;

    return (
      <div className={styles.bannerAdWrap} data-base-banner-device="desktop">
        <div className={styles.bannerAdBox}>
          <div className={styles.adHeader}>
            <span className={styles.sponsored}>Sponsored</span>
          </div>

          <GamDisplaySlot
            id="base-desktop-banner"
            adUnit={GAM_BASE_DESKTOP_BANNER_AD_UNIT}
            sizes={[...GAM_BASE_DESKTOP_BANNER_SIZES]}
            minHeight={GAM_BASE_DESKTOP_BANNER_MIN_HEIGHT}
            targeting={{
              page_type: "base",
              device: "desktop",
              position: "before_strengths",
              placement: "base_desktop_banner_before_strengths",
              ...targeting,
            }}
          />
        </div>
      </div>
    );
  }

  return null;
}
